import { Header } from '@/components/header'
import { Footer } from '@/components/footer'
import { AssociationsAndSuppliers } from '@/components/AssociationsAndSuppliers'
import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'

// Types for images
interface ImageItem {
  src: string
  alt: string
  type: 'pergola' | 'privacy-wall'
}

export default function PergolasAndPrivacyWallsGallery() {
  const [displayedImages, setDisplayedImages] = useState<ImageItem[]>([])
  
  // State for lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxSlide, setLightboxSlide] = useState(0)
  const [lightboxAnimation, setLightboxAnimation] = useState('entering')
  
  // State for FAQ Accordion
  const [openFaq, setOpenFaq] = useState<number | null>(0)

  const faqs = [
    {
      q: "Do I need a permit to build a pergola in Calgary?",
      a: "In most cases, the City of Calgary does not require a building permit for a freestanding pergola if it is under 10 square meters (107 sq. ft.) and not attached to your home. We verify all local building codes and handle permitting before construction begins."
    },
    {
      q: "How much does a custom pergola cost in Calgary?",
      a: "Pricing depends heavily on the footprint and materials chosen. Pressure-treated wood is the most cost-effective option, while Western Red Cedar is a premium investment. We offer transparent onsite estimates and flexible financing starting at $99 a month to fit your budget."
    },
    {
      q: "What is the best material for a pergola in Alberta?",
      a: "Western Red Cedar is the optimal choice for Alberta's climate due to its natural resistance to rot, decay, and insect damage. It withstands heavy snow loads and intense summer UV exposure exceptionally well."
    },
    {
      q: "Will a custom pergola withstand Calgary's heavy snow loads?",
      a: "Yes. All our custom pergolas are structurally engineered for Calgary’s specific climate. We use robust fir or cedar cross beams and thick rafters to ensure your shade structure safely supports heavy winter snow accumulations without bowing or structural failure."
    },
    {
      q: "How do I maintain my cedar or pressure-treated pergola?",
      a: "To protect against UV fading and moisture, we recommend applying a high-quality UV-blocking stain or water repellent every 2 to 3 years. This simple maintenance prevents the wood from greying and significantly extends the lifespan of your shade structure."
    },
    {
      q: "Do I need HOA approval to build a privacy wall or pergola?",
      a: "In many Calgary communities, Homeowner Association (HOA) or architectural control approval is required before building a structure above a certain height. We provide detailed drawings and specifications to help streamline your community approval process."
    },
    {
      q: "How long does it take to install a custom pergola or privacy wall?",
      a: "Once your custom design is approved and materials are secured, our dedicated in-house crews typically complete construction within 2 to 4 days, depending on the complexity of the cross beams, rafters, and overall size."
    }
  ]

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index)
  }
  
  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault()
    window.history.pushState({}, '', href)
    window.dispatchEvent(new Event('popstate'))
    window.scrollTo(0, 0)
  }

  function parseImageFileName(filename: string): string {
    const withoutExtension = filename.substring(0, filename.lastIndexOf('.'))
    return withoutExtension.replace(/-/g, ' ')
  }

  function fixImagePath(path: string): string {
    let imagePath = path.replace('/public', '')
    if (!imagePath.startsWith('/')) {
      imagePath = '/' + imagePath
    }
    return imagePath
  }

  useEffect(() => {
    try {
      const pergolaImages = import.meta.glob('/assets/images/Pergola/**/*.{jpg,jpeg,png,webp}', { eager: true })
      const privacyWallImages = import.meta.glob('/assets/images/Privacy Walls/**/*.{jpg,jpeg,png,webp}', { eager: true })
      
      const loadedImages: ImageItem[] = []

      Object.keys(pergolaImages).forEach(path => {
        const imagePath = fixImagePath(path)
        const fileName = path.substring(path.lastIndexOf('/') + 1)
        const altText = parseImageFileName(fileName)
        
        loadedImages.push({
          src: imagePath,
          alt: altText,
          type: 'pergola'
        })
      })
      
      Object.keys(privacyWallImages).forEach(path => {
        const imagePath = fixImagePath(path)
        const fileName = path.substring(path.lastIndexOf('/') + 1)
        const altText = parseImageFileName(fileName)
        
        loadedImages.push({
          src: imagePath,
          alt: altText,
          type: 'privacy-wall'
        })
      })

      const shuffledImages = loadedImages.sort(() => Math.random() - 0.5)
      setDisplayedImages(shuffledImages)
    } catch (error) {
      console.error('Error loading images:', error)
    }
  }, [])
  
  const openLightbox = (index: number) => {
    setLightboxSlide(index)
    setLightboxOpen(true)
    setLightboxAnimation('entering')
    setTimeout(() => setLightboxAnimation('visible'), 50)
    document.body.style.overflow = 'hidden'
  }
  
  const closeLightbox = () => {
    setLightboxAnimation('exiting')
    setTimeout(() => {
      setLightboxOpen(false)
      document.body.style.overflow = 'visible'
    }, 300) 
  }
  
  const goToPrevLightboxSlide = () => {
    setLightboxSlide((prev) => 
      prev === 0 ? displayedImages.length - 1 : prev - 1
    )
  }
  
  const goToNextLightboxSlide = () => {
    setLightboxSlide((prev) => 
      (prev + 1) % displayedImages.length
    )
  }
  
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightboxOpen) return
      
      switch (e.key) {
        case 'ArrowLeft':
          goToPrevLightboxSlide()
          break
        case 'ArrowRight':
          goToNextLightboxSlide()
          break
        case 'Escape':
          closeLightbox()
          break
        default:
          break
      }
    }
    
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightboxOpen, displayedImages.length])

  // Consolidated Schema Data for Pergolas & Privacy Walls
  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "HomeAndConstructionBusiness",
        "@id": "https://busybeaverconstruction.com/#organization",
        "name": "Busy Beaver Construction",
        "url": "https://busybeaverconstruction.com",
        "logo": "https://busybeaverconstruction.com/assets/images/2020/03/busy-beaver-logo.png",
        "description": "Calgary's premier custom deck, pergola, and privacy wall builder with over 28 Years of experience.",
        "areaServed": "Calgary, AB",
        "sameAs": [
          "https://www.bbb.org/ca/ab/calgary/profile/deck-builder/busy-beaver-construction-ltd-0017-38381"
        ]
      },
      {
        "@type": "Service",
        "serviceType": "Pergola and Privacy Wall Construction",
        "provider": {"@id": "https://busybeaverconstruction.com/#organization"},
        "areaServed": "Calgary, AB",
        "description": "Custom Western Red Cedar and pressure-treated wood pergolas, shade structures, and privacy walls for Calgary homeowners.",
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Outdoor Structures",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Custom Cedar Pergola Installation"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Pressure Treated Wood Shade Structures"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Custom Privacy Wall Construction"
              }
            }
          ]
        }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://busybeaverconstruction.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Services",
            "item": "https://busybeaverconstruction.com/services"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "Pergolas & Privacy Walls",
            "item": "https://busybeaverconstruction.com/services/pergolas-and-privacy-walls"
          }
        ]
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.a
          }
        }))
      }
    ]
  };

  return (
    <main className="bg-gray-50/50">
      <Helmet>
        <title>Calgary Pergola Builders & Custom Privacy Walls | Busy Beaver</title>
        <meta name="description" content="Looking for expert pergola builders in Calgary? Busy Beaver Construction builds custom cedar and pressure-treated shade structures, including arbors. Get a free estimate today!" />
        <meta name="keywords" content="Calgary pergola builders, cedar pergolas Calgary, custom privacy walls, backyard shade structures, horizontal slat fences" />
        <link rel="canonical" href="https://busybeaverconstruction.com/services/pergolas-and-privacy-walls" />
        <meta property="og:title" content="Calgary Pergola Builders & Custom Privacy Walls | Busy Beaver" />
        <meta property="og:description" content="Looking for expert pergola builders in Calgary? Busy Beaver Construction builds custom cedar and pressure-treated shade structures. Get a free estimate today!" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://busybeaverconstruction.com/services/pergolas-and-privacy-walls" />
        <meta property="og:image" content="https://busybeaverconstruction.com/assets/images/Pergola/Modern-Cedar-Pergoal-with-2x8-cross-beams-and-1x4-Cedar-rafters.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="preload" href="/assets/images/Privacy Walls/Cedar-2x6-Horiztonal-slat-Privacy-wall.jpg" as="image" />
      </Helmet>

      {/* JSON-LD Schema placed OUTSIDE Helmet to ensure rendering */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />

      <Header />
      
      {/* Premium Hero Banner */}
      <section className="relative h-[500px] lg:h-[600px] flex items-center justify-center overflow-hidden">
        <img 
          src="/assets/images/Privacy Walls/Cedar-2x6-Horiztonal-slat-Privacy-wall.jpg"
          alt="Custom cedar 2x6 horizontal slat privacy wall installation by Busy Beaver Construction Calgary"
          className="absolute inset-0 w-full h-full object-cover object-center loading-eager"
          fetchPriority="high"
        />        
        {/* Rich gradient overlay for better text legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-gray-900/90"></div>
        
        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto mt-16 lg:mt-24">
          <span className="inline-block py-1 px-3 rounded-full bg-beaver-green/20 border border-beaver-green/50 text-beaver-green font-bold tracking-widest uppercase text-xs md:text-sm mb-6 backdrop-blur-sm">
            Transform Your Outdoors
          </span>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-primary text-white mb-6 drop-shadow-lg leading-tight">
            Calgary Pergola Builders
          </h1>
          <p className="text-gray-200 text-lg md:text-xl font-text mb-10 max-w-2xl mx-auto font-light leading-relaxed">
            Expertly engineered cedar and pressure-treated shade structures built to withstand Alberta’s climate. Quality workmanship that ensures lasting value.
          </p>
          <a
            href="/estimate" 
            className="inline-flex items-center gap-3 px-8 py-4 bg-beaver-green text-black font-bold uppercase tracking-wider rounded shadow-xl hover:bg-white hover:scale-105 transition-all duration-300 font-accent"
            onClick={(e) => handleNavigation(e, '/estimate')}
          >
            Get Your Free Estimate <i className="fas fa-arrow-right"></i>
          </a>
        </div>
      </section>

      {/* Commercial Service Section (Materials) */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex flex-col items-center mb-16 text-center">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-primary text-gray-900 mb-6">Expert Pergola & Privacy Wall Construction</h2>
            <div className="w-24 h-1 bg-beaver-green mb-6 rounded-full"></div>
            <p className="text-lg text-gray-600 max-w-4xl font-text leading-relaxed">
               Busy Beaver Construction is Calgary’s top-rated pergola builder. We design and install custom Western Red Cedar and pressure-treated shade structures, including standalone arbors. Backed by 25+ years of experience and an A+ BBB rating, our in-house crews build pergolas expertly engineered to withstand Alberta’s heavy snow loads and extreme UV exposure.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Column: Materials Cards */}
            <div className="space-y-10">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6 font-secondary flex items-center">
                  Premium Materials for Alberta's Climate
                </h3>
                
                <div className="space-y-5 mt-6">
                  {/* Card 1 */}
                  <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300">
                    <div className="flex items-start">
                      <div className="bg-beaver-green/10 w-12 h-12 rounded-full flex items-center justify-center mr-5 shrink-0">
                        <i className="fas fa-tree text-beaver-green text-xl"></i>
                      </div>
                      <div>
                        <strong className="text-gray-900 block text-lg mb-1 font-secondary">Western Red Cedar</strong>
                        <p className="text-gray-600 font-text leading-relaxed text-sm md:text-base">The optimal choice for local weather. Highly rot-resistant with a stunning rustic finish that naturally resists warping during intense Calgary freeze-thaw cycles.</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Card 2 */}
                  <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300">
                    <div className="flex items-start">
                      <div className="bg-beaver-green/10 w-12 h-12 rounded-full flex items-center justify-center mr-5 shrink-0">
                        <i className="fas fa-hammer text-beaver-green text-xl"></i>
                      </div>
                      <div>
                        <strong className="text-gray-900 block text-lg mb-1 font-secondary">Pressure-Treated Wood</strong>
                        <p className="text-gray-600 font-text leading-relaxed text-sm md:text-base">A highly durable and cost-effective alternative. When properly stained, it provides an incredibly robust frame for large backyard shade structures.</p>
                      </div>
                    </div>
                  </div>

                  {/* Card 3 */}
                  <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300">
                    <div className="flex items-start">
                      <div className="bg-beaver-green/10 w-12 h-12 rounded-full flex items-center justify-center mr-5 shrink-0">
                        <i className="fas fa-layer-group text-beaver-green text-xl"></i>
                      </div>
                      <div>
                        <strong className="text-gray-900 block text-lg mb-1 font-secondary">Structural Components</strong>
                        <p className="text-gray-600 font-text leading-relaxed text-sm md:text-base">We utilize solid fir wood cross beams, custom-notched rafters, and sleek aluminum accents to match your home's exterior architecture.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right Column: Featured Material Image */}
            <div className="h-full relative">
              {/* Decorative accent box behind image */}
              <div className="absolute -inset-4 bg-gray-100 rounded-2xl transform rotate-2 hidden lg:block"></div>
              
              <div className="overflow-hidden h-[450px] md:h-[600px] rounded-2xl shadow-xl relative group z-10">
                <img 
                  src="/assets/images/Pergola/Modern-Cedar-Pergoal-with-2x8-cross-beams-and-1x4-Cedar-rafters.jpg"
                  alt="Modern Cedar Pergola with 2x8 cross beams built by Busy Beaver"
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <div className="bg-beaver-green w-12 h-1 mb-4 rounded-full"></div>
                  <p className="text-white font-secondary text-2xl font-bold">Western Red Cedar</p>
                  <p className="text-gray-200 font-text text-sm mt-2">Featuring 2x8 Cross Beams & Custom Rafters</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Custom Privacy Walls Section */}
      <section className="py-20 lg:py-28 bg-gray-50 border-y border-gray-200 overflow-hidden">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center flex-col-reverse lg:flex-row">
            
            {/* Left Column: Image */}
            <div className="order-2 lg:order-1 relative">
              <div className="absolute -inset-4 bg-white rounded-2xl shadow-sm transform -rotate-2 hidden lg:block"></div>
              <div className="overflow-hidden h-[450px] md:h-[550px] rounded-2xl shadow-xl relative group z-10 border-4 border-white">
                <img 
                  src="/assets/images/2020/04/IMG_2248.jpg"
                  alt="Custom privacy wall and deck integration by Calgary builders"
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                />
              </div>
            </div>

            {/* Right Column: Content */}
            <div className="order-1 lg:order-2 space-y-8">
              <div>
                <span className="text-beaver-red font-bold tracking-widest uppercase text-sm mb-3 block">Enhance Your Space</span>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-primary text-gray-900 leading-tight">Custom Privacy Walls & Deck Integrations</h2>
              </div>
              
              <p className="font-text text-gray-600 text-lg leading-relaxed">
                Custom privacy walls provide essential wind protection and visual seclusion for Calgary homeowners. We seamlessly integrate horizontal slat, vertical picket, and lattice privacy screens directly into your new or existing deck, expanding your usable outdoor living space with modular, highly functional designs.
              </p>
              
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 mt-6">
                <ul className="space-y-4 font-text text-gray-700">
                  <li className="flex items-start">
                    <i className="fas fa-wind text-beaver-green mt-1 mr-4 text-xl w-6 text-center"></i>
                    <div>
                      <strong className="block text-gray-900">Wind Mitigation</strong>
                      <span className="text-sm text-gray-600">Block harsh Alberta winds to enjoy your deck longer into the season.</span>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <i className="fas fa-eye-slash text-beaver-green mt-1 mr-4 text-xl w-6 text-center"></i>
                    <div>
                      <strong className="block text-gray-900">Backyard Seclusion</strong>
                      <span className="text-sm text-gray-600">Block sightlines from neighboring properties for total privacy.</span>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <i className="fas fa-paint-roller text-beaver-green mt-1 mr-4 text-xl w-6 text-center"></i>
                    <div>
                      <strong className="block text-gray-900">Aesthetic Matching</strong>
                      <span className="text-sm text-gray-600">Stained to match your deck or house trim perfectly.</span>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 lg:py-28 bg-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-[url('/assets/images/2020/04/IMG_2248.jpg')] bg-cover bg-center bg-fixed"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-gray-900/95 to-gray-900"></div>
        
        <div className="container mx-auto px-4 max-w-7xl relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-primary text-white mb-6">Why Choose Busy Beaver Construction?</h2>
            <div className="w-24 h-1 bg-beaver-red mb-6 rounded-full mx-auto"></div>
            <p className="text-gray-300 font-text text-lg max-w-3xl mx-auto font-light leading-relaxed">
              We are Calgary's most trusted pergola and privacy wall contractors, built on a foundation of local expertise and unwavering integrity.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-800/50 p-8 rounded-2xl border border-gray-700/50 hover:border-beaver-green/50 hover:-translate-y-2 transition-all duration-300 backdrop-blur-sm group">
              <div className="bg-gray-900 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 border border-gray-700">
                <i className="fas fa-calendar-check text-2xl text-beaver-green"></i>
              </div>
              <h3 className="text-2xl font-bold font-secondary mb-4 text-white group-hover:text-beaver-green transition-colors">25+ Years Experience</h3>
              <p className="text-gray-400 font-text leading-relaxed text-sm md:text-base">Decades of dedicated outdoor construction expertise in Calgary, directly translated into the structural integrity of your backyard project.</p>
            </div>
            
            <div className="bg-gray-800/50 p-8 rounded-2xl border border-gray-700/50 hover:border-beaver-green/50 hover:-translate-y-2 transition-all duration-300 backdrop-blur-sm group">
              <div className="bg-gray-900 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 border border-gray-700">
                <i className="fas fa-star text-2xl text-beaver-green"></i>
              </div>
              <h3 className="text-2xl font-bold font-secondary mb-4 text-white group-hover:text-beaver-green transition-colors">A+ BBB Rating</h3>
              <p className="text-gray-400 font-text leading-relaxed text-sm md:text-base">A flawless track record with the Better Business Bureau, proving our uncompromising commitment to customer satisfaction and transparency.</p>
            </div>
            
            <div className="bg-gray-800/50 p-8 rounded-2xl border border-gray-700/50 hover:border-beaver-green/50 hover:-translate-y-2 transition-all duration-300 backdrop-blur-sm group">
              <div className="bg-gray-900 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 border border-gray-700">
                <i className="fas fa-hard-hat text-2xl text-beaver-green"></i>
              </div>
              <h3 className="text-2xl font-bold font-secondary mb-4 text-white group-hover:text-beaver-green transition-colors">In-House Crews</h3>
              <p className="text-gray-400 font-text leading-relaxed text-sm md:text-base">We strictly use our own dedicated, highly-trained professionals—never subcontractors. This guarantees total compliance with local building codes.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Process Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center mb-20">
            <span className="text-beaver-red font-bold tracking-widest uppercase text-sm mb-3 block">How It Works</span>
            <h2 className="text-3xl lg:text-5xl font-primary text-gray-900 mb-6">Our 4-Step Installation Process</h2>
            <p className="text-gray-600 max-w-3xl mx-auto font-text text-lg">
              Our streamlined pergola installation process ensures a seamless, code-compliant build. Backed by 28 Years of experience, our in-house crews handle everything from initial design to city permitting.
            </p>
          </div>
          
          <div className="relative">
            {/* Desktop Connecting Line */}
            <div className="hidden md:block absolute top-12 left-[10%] right-[10%] h-0.5 bg-gray-200 -z-10"></div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-6 relative z-10">
              
              {/* Step 1 */}
              <div className="text-center group">
                <div className="w-24 h-24 bg-white border-4 border-gray-100 group-hover:border-beaver-green text-gray-400 group-hover:text-beaver-green rounded-full flex items-center justify-center mx-auto mb-6 text-3xl font-bold font-secondary shadow-lg transition-all duration-300 group-hover:scale-110">
                  1
                </div>
                <h3 className="font-secondary font-bold text-xl mb-3 text-gray-900">Free Onsite Estimate</h3>
                <p className="text-gray-600 font-text leading-relaxed px-2">We assess your yard, measure the space, and discuss materials (Cedar vs PT) to fit your budget.</p>
              </div>

              {/* Step 2 */}
              <div className="text-center group">
                <div className="w-24 h-24 bg-white border-4 border-gray-100 group-hover:border-beaver-green text-gray-400 group-hover:text-beaver-green rounded-full flex items-center justify-center mx-auto mb-6 text-3xl font-bold font-secondary shadow-lg transition-all duration-300 group-hover:scale-110">
                  2
                </div>
                <h3 className="font-secondary font-bold text-xl mb-3 text-gray-900">Design & Permits</h3>
                <p className="text-gray-600 font-text leading-relaxed px-2">We finalize the custom design and handle all City of Calgary bylaw checks and required permits.</p>
              </div>

              {/* Step 3 */}
              <div className="text-center group">
                <div className="w-24 h-24 bg-white border-4 border-gray-100 group-hover:border-beaver-green text-gray-400 group-hover:text-beaver-green rounded-full flex items-center justify-center mx-auto mb-6 text-3xl font-bold font-secondary shadow-lg transition-all duration-300 group-hover:scale-110">
                  3
                </div>
                <h3 className="font-secondary font-bold text-xl mb-3 text-gray-900">Professional Build</h3>
                <p className="text-gray-600 font-text leading-relaxed px-2">Our dedicated in-house crews complete standard pergola installations within 2 to 4 days.</p>
              </div>

              {/* Step 4 */}
              <div className="text-center group">
                <div className="w-24 h-24 bg-white border-4 border-gray-100 group-hover:border-beaver-green text-gray-400 group-hover:text-beaver-green rounded-full flex items-center justify-center mx-auto mb-6 text-3xl font-bold font-secondary shadow-lg transition-all duration-300 group-hover:scale-110">
                  4
                </div>
                <h3 className="font-secondary font-bold text-xl mb-3 text-gray-900">Final Walkthrough</h3>
                <p className="text-gray-600 font-text leading-relaxed px-2">We clean the site entirely and conduct a walkthrough to ensure 100% satisfaction.</p>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Financing Banner */}
      <section className="py-16 bg-beaver-green relative overflow-hidden">
        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#000_1px,transparent_1px)] [background-size:20px_20px]"></div>
        
        <div className="container mx-auto px-4 text-center max-w-5xl relative z-10">
          <h2 className="text-3xl md:text-5xl font-primary text-gray-900 mb-6 drop-shadow-sm">Finance Your Backyard Oasis</h2>
          <p className="text-xl md:text-2xl text-gray-800 font-text mb-10 font-medium">
            Through our partnership with Financeit, you can build your custom pergola or privacy wall for as low as <strong className="text-black font-black bg-white/40 px-3 py-1 rounded-lg shadow-sm">$99 / month</strong>.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6">
            <a
              href="/estimate" 
              className="px-10 py-5 bg-gray-900 text-white font-bold uppercase tracking-widest rounded-lg shadow-xl hover:bg-black hover:scale-105 transition-all duration-300 font-accent w-full sm:w-auto"
              onClick={(e) => handleNavigation(e, '/estimate')}
            >
              Apply For Financing
            </a>
            <p className="text-gray-800 text-sm italic font-medium">*Subject to credit approval. Ask us for details during your estimate.</p>
          </div>
        </div>
      </section>

      {/* Gazebo Cross-Link Section */}
      <section className="py-12 bg-gray-50 border-t border-gray-100">
        <div className="container mx-auto px-4 text-center max-w-4xl">
          <p className="text-lg text-gray-700 font-text leading-relaxed">
            Looking for a fully covered outdoor structure? Explore our <a href="/services/gazebo" className="text-beaver-green font-bold hover:underline" onClick={(e) => handleNavigation(e, '/services/gazebo')}>custom gazebo builders in Calgary</a> for year-round protection and enclosed backyard living.
          </p>
        </div>
      </section>

      {/* Interactive Gallery Content */}
      <section className="py-20 lg:py-28 bg-white border-t border-gray-100">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex flex-col items-center mb-16">
            <h2 className="text-center text-4xl lg:text-5xl font-primary text-gray-900">Pergolas & Privacy Walls Gallery</h2>
            <div className="w-24 h-1 bg-beaver-green my-6 rounded-full"></div>
            <p className="text-gray-500 font-text text-lg">Browse our latest outdoor structure projects in Calgary.</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {displayedImages.map((image, index) => (
              <div 
                key={index} 
                className="overflow-hidden rounded-xl shadow-sm hover:shadow-2xl transition-all duration-500 bg-white cursor-pointer border border-gray-100 group relative"
                onClick={() => openLightbox(index)}
              >
                <div className="h-[300px] md:h-[350px] overflow-hidden relative">
                  <img 
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover loading-lazy transition-transform duration-700 group-hover:scale-110"
                  />
                  {/* Hover Overlay with Icon */}
                  <div className="absolute inset-0 bg-gray-900/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="w-16 h-16 bg-beaver-green/90 rounded-full flex items-center justify-center transform scale-50 group-hover:scale-100 transition-transform duration-300 delay-100">
                      <i className="fas fa-search-plus text-white text-2xl"></i>
                    </div>
                  </div>
                </div>
                <div className="p-5 border-t border-gray-100 bg-white">
                  <p className="text-sm text-gray-700 font-text font-medium truncate group-hover:text-beaver-green transition-colors">
                    {image.alt} 
                  </p>
                </div>
              </div>
            ))}
          </div>
          
          {displayedImages.length === 0 && (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-gray-200 border-t-beaver-green rounded-full animate-spin mb-4"></div>
              <p className="text-gray-500 font-text">Loading gallery images...</p>
            </div>
          )}
        </div>
      </section>

      {/* Elegant FAQ Accordion */}
      <section className="py-20 lg:py-28 bg-gray-50 border-t border-gray-200">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-16">
            <span className="text-beaver-red font-bold tracking-widest uppercase text-sm mb-3 block">Knowledge Base</span>
            <h2 className="text-3xl md:text-5xl font-primary text-gray-900">
              Frequently Asked Questions
            </h2>
            <div className="w-24 h-1 bg-gray-300 mx-auto mt-6 rounded-full"></div>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:border-beaver-green/50 transition-colors duration-300">
                <button 
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 md:px-8 py-6 text-left flex justify-between items-center focus:outline-none"
                  aria-expanded={openFaq === index}
                >
                  <h3 className={`text-lg md:text-xl font-bold font-secondary pr-6 transition-colors duration-300 ${openFaq === index ? 'text-beaver-green' : 'text-gray-800'}`}>
                    {faq.q}
                  </h3>
                  <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${openFaq === index ? 'bg-beaver-green/10 text-beaver-green' : 'bg-gray-100 text-gray-400'}`}>
                    <i className={`fas fa-chevron-down transition-transform duration-500 ${openFaq === index ? '-rotate-180' : ''}`}></i>
                  </div>
                </button>
                <div 
                  className={`px-6 md:px-8 overflow-hidden transition-all duration-500 ease-in-out ${openFaq === index ? 'max-h-96 pb-8 opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <p className="text-gray-600 font-text leading-relaxed text-base md:text-lg border-t border-gray-100 pt-6">
                    {faq.a}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Associations and Footer */}
      <AssociationsAndSuppliers />
      <Footer />
      
      {/* Lightbox */}
      {lightboxOpen && displayedImages.length > 0 && (
        <div 
          className={`fixed inset-0 bg-gray-900/95 z-50 flex items-center justify-center transition-opacity duration-300 backdrop-blur-sm ${
            lightboxAnimation === 'entering' ? 'opacity-0' : 
            lightboxAnimation === 'exiting' ? 'opacity-0' : 'opacity-100'
          }`}
        >
          <div 
            className="absolute inset-0"
            onClick={closeLightbox}
          />
          
          <div className="relative max-w-7xl w-full h-screen max-h-screen flex items-center justify-center p-4">
            <button 
              onClick={closeLightbox}
              className={`absolute top-6 right-6 z-10 bg-white/10 hover:bg-beaver-red text-white w-12 h-12 rounded-full transition-all duration-300 focus:outline-none backdrop-blur-md flex items-center justify-center ${
                lightboxAnimation === 'entering' ? 'opacity-0 translate-y-[-20px]' : 
                lightboxAnimation === 'exiting' ? 'opacity-0 translate-y-[-20px]' : 'opacity-100 translate-y-0'
              }`}
              aria-label="Close lightbox"
            >
              <i className="fas fa-times text-xl"></i>
            </button>
            
            <div className="relative w-full h-full flex items-center justify-center">
              <img
                src={displayedImages[lightboxSlide].src}
                alt={displayedImages[lightboxSlide].alt}
                className={`max-w-full max-h-[85vh] object-contain transition-all duration-500 shadow-2xl rounded ${
                  lightboxAnimation === 'entering' ? 'opacity-0 scale-95' : 
                  lightboxAnimation === 'exiting' ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
                }`}
              />
              
              <div 
                className={`absolute bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-900/80 backdrop-blur-md text-white py-3 px-8 rounded-full text-center max-w-2xl w-[90%] transition-all duration-500 font-text shadow-2xl border border-gray-700/50 ${
                  lightboxAnimation === 'entering' ? 'opacity-0 translate-y-[20px]' : 
                  lightboxAnimation === 'exiting' ? 'opacity-0 translate-y-[20px]' : 'opacity-100 translate-y-0'
                }`}
              >
                <span className="truncate block md:inline">{displayedImages[lightboxSlide].alt}</span>
                <span className="mt-1 md:mt-0 md:ml-3 text-xs text-beaver-green font-bold uppercase tracking-wider block md:inline">
                  ({displayedImages[lightboxSlide].type === 'pergola' ? 'Pergola' : 'Privacy Wall'})
                </span>
              </div>
              
              <button 
                onClick={(e) => {
                  e.stopPropagation()
                  goToPrevLightboxSlide()
                }}
                className={`absolute left-[10px] md:left-[40px] lg:left-[20px] xl:left-[-60px] top-1/2 transform -translate-y-1/2 bg-white/10 hover:bg-beaver-green text-white w-14 h-14 rounded-full transition-all duration-300 focus:outline-none backdrop-blur-md flex items-center justify-center ${
                  lightboxAnimation === 'entering' ? 'opacity-0 translate-x-[-20px]' : 
                  lightboxAnimation === 'exiting' ? 'opacity-0 translate-x-[-20px]' : 'opacity-100 translate-x-0'
                }`}
                aria-label="Previous slide"
              >
                <i className="fas fa-chevron-left text-xl"></i>
              </button>
              
              <button 
                onClick={(e) => {
                  e.stopPropagation()
                  goToNextLightboxSlide()
                }}
                className={`absolute right-[10px] md:right-[40px] lg:right-[20px] xl:right-[-60px] top-1/2 transform -translate-y-1/2 bg-white/10 hover:bg-beaver-green text-white w-14 h-14 rounded-full transition-all duration-300 focus:outline-none backdrop-blur-md flex items-center justify-center ${
                  lightboxAnimation === 'entering' ? 'opacity-0 translate-x-[20px]' : 
                  lightboxAnimation === 'exiting' ? 'opacity-0 translate-x-[20px]' : 'opacity-100 translate-x-0'
                }`}
                aria-label="Next slide"
              >
                <i className="fas fa-chevron-right text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  )
}